/**
 * Payment Service
 * Handles payment initiation and status checking
 */

const PAYMENT_STATUS = {
  PENDING: 202,
  COMPLETED: 200,
  FAILED: 400,
};

const PAYMENT_TYPES = {
  MOMO: 'pay_momo',
  CASH: 'pay_cash',
};

class PaymentService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl || 'https://applicationbackend.jalikoi.rw/api/v2';
  }

  /**
   * Initiate payment
   * @param {Object} paymentData - Payment information
   * @returns {Promise<Object>} Payment response with transaction_id
   */
  async initiatePayment(paymentData) {
    const { applicant_id, type, phone, file_id } = paymentData;

    try {
      const response = await fetch(`${this.baseUrl}/application/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          step: 8,
          applicant_id,
          type,
          phone,
          file_id,
        }),
      });

      const data = await response.json();

    

      if (!response.ok) {
        throw new Error(data.message || 'Payment initiation failed');
      }

      return {
        success: true,
        transaction_id: data.transaction_id,
        message: data.message || 'Payment initiated successfully',
        data,
      };
    } catch (error) {
   
      return {
        success: false,
        message: error.message,
      };
    }
  }

  /**
   * Check payment status
   * @param {string} applicantId - Applicant ID
   * @param {string} transactionId - Transaction ID
   * @returns {Promise<Object>} Payment status
   */
  async checkPaymentStatus(applicantId, transactionId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/application/payment-status/${applicantId}/${transactionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

    

      return {
        status: response.data?.status,
        isPending: response.data?.status === PAYMENT_STATUS.PENDING,
        isCompleted: response.data?.status === PAYMENT_STATUS.COMPLETED,
        isFailed: response.data?.status === PAYMENT_STATUS.FAILED,
        data,
      };
    } catch (error) {
      console.error('Payment status check error:', error);
      return {
        status: null,
        isPending: false,
        isCompleted: false,
        isFailed: true,
        error: error.message,
      };
    }
  }

  /**
   * Poll payment status until completed or timeout
   * @param {string} applicantId - Applicant ID
   * @param {string} transactionId - Transaction ID
   * @param {Object} options - Polling options
   * @returns {Promise<Object>} Final payment status
   */
  async pollPaymentStatus(
    applicantId,
    transactionId,
    options = {}
  ) {
    const {
      interval = 3000, // Check every 3 seconds
      maxAttempts = 40, // Max 2 minutes (40 * 3 seconds)
      onStatusUpdate = null, // Callback for status updates
    } = options;

    let attempts = 0;

   

    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        attempts++;
   

        try {
          const statusResult = await this.checkPaymentStatus(
            applicantId,
            transactionId
          );

     

          // Call status update callback if provided
          if (onStatusUpdate) {
            onStatusUpdate(statusResult, attempts);
          }

          // Payment completed successfully
          if (statusResult.isCompleted) {
         
            resolve({
              success: true,
              message: 'Ubwishyu bwawe bwemejwe neza',
              attempts,
              data: statusResult.data,
            });
            return;
          }

          // Payment failed
          if (statusResult.isFailed) {
         
            resolve({
              success: false,
              message: 'Ubwishyu bwawe ntibwagenze neza. Ongera ugerageze.',
              attempts,
              data: statusResult.data,
            });
            return;
          }

          // Max attempts reached
          if (attempts >= maxAttempts) {
          
            resolve({
              success: false,
              message: 'Igihe cyarangiye. Nyamuneka reba niba ubwishyu bwakozwe.',
              timeout: true,
              attempts,
            });
            return;
          }

          // Continue polling if still pending
          if (statusResult.isPending) {
          
            setTimeout(checkStatus, interval);
          } else {
            // Unexpected status
         
            setTimeout(checkStatus, interval);
          }
        } catch (error) {
        
          reject({
            success: false,
            message: error.message,
            attempts,
          });
        }
      };

      checkStatus();
    });
  }

  /**
   * Check if payment type requires mobile money
   * @param {string} paymentType - Payment type
   * @returns {boolean}
   */
  isMobileMoneyPayment(paymentType) {
    return paymentType === PAYMENT_TYPES.MOMO;
  }
}

export default PaymentService;
export { PAYMENT_STATUS, PAYMENT_TYPES };